import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  Gyg365RestoList,
  InitialFilter,
  UpdateResto,
  RestaurantWithUsersResponse,
  RestaurantWithUsers
} from 'src/types/apps/gyg365RestoListTypes'
import { Redux } from 'src/store'
import {
  EXPORT_RESTO365_RESTAURANT,
  RESTO365_ALL_RESTAURANT,
  RESTO365_ADMIN_LIST_RESTAURANT,
  RESTO365_GET_RESTAURANT_BY_ID,
  RESTO365_RESTAURANT,
  RESTO365_RESTAURANT_COMMON_SEARCH,
  RESTO365_RESTAURANT_COMMON_SEARCH_UPDATE,
  RESTO365_RESTAURANT_CREATE,
  RESTO365_RESTAURANT_CREATE_LICENSE_LIST,
  RESTO365_RESTAURANT_ERROR_RESET,
  RESTO365_RESTAURANT_LICENSE_LIST,
  RESTO365_RESTAURANT_RESET_REDUCER,
  RESTO365_RESTAURANT_UPDATE_FRANCHISE_ID,
  RESTO365_RESTAURANT_UPDATE_LICENSE_LIST,
  RESTO365_RESTAURANT_UPDATE_LICENSE_UPLOAD,
  RESTO365_UPDATE_RESTAURANT,
  RESTO365_UPDATE_RESTAURANT_SUSTAINABILITY,
  RESTO365_GET_FRANCHISE_RESTAURANTS_BY_IDS,
  RESTO365_VALIDATE_YEXT_ENTITY_ID,
  RESTO365_NRO_RESTAURANT,
  RESTO365_PERM_CLOSED_RESTAURANT,
  RESTO365_VALIDATE_GOOGLE_PLACES_ADDRESS
} from './actions'
import {
  MenuInitialState,
  getmenuSectionsList,
  enableDisableProduct,
  getProducts,
  getChannelList,
  getStoreMenu
} from '../gygMenu'
import { LicenseCreate, LicenseUpdate } from '@/src/types/apps/gygLicense'
import type { SectionProduct } from '@/src/types/apps/gygMenu'
import { QueryParams } from '@/src/types/apps/queryParams'

interface InitialState extends MenuInitialState {
  gyg365restros: Gyg365RestoList[]
  exportGyg365restro: Gyg365RestoList[]
  allocationRestaurants: Gyg365RestoList[]
  gyg365resto: Gyg365RestoList[]
  restoData: Gyg365RestoList | undefined
  restoDataWithUsers: RestaurantWithUsersResponse['data'] | undefined
  gygLicenses: LicenseUpdate[]
  gygLicense: LicenseUpdate | undefined
  pending: boolean
  createStorePending: boolean
  get365restroPending: boolean
  get365restroWithUsersPending: boolean
  export365restroPending: boolean
  allocationRestaurantsPending: boolean
  update365restroPending: boolean
  updateRestaurantIsActivePending: boolean
  updateRestaurantSustainabilityPending: boolean
  validateYextEntityIdPending: boolean
  yextValidationError: string | null
  yextValidationSuccess: boolean
  yextValidationErrorState: boolean

  // Google Places validation state
  validateGooglePlacesAddressPending: boolean
  googlePlacesValidationResult: GooglePlacesAddressResponse | null
  googlePlacesValidationError: string | null
  errors: any
  success?: string
  commonSearchData: InitialFilter
  restaurantMeta: { totalRows: number; pages: number } | null

  // New state for NRO and Perm Closed restaurants
  nroRestaurants: Gyg365RestoList[]
  permClosedRestaurants: Gyg365RestoList[]
  nroRestaurantsPending: boolean
  permClosedRestaurantsPending: boolean
  nroRestaurantsMeta: {
    totalRows: number
    pages: number
    statistics?: {
      thisWeek: {
        inPipeline: number
        openedSoFar: number
        driveThru: number
        is24By7: number
        franchise: number
        corporate: number
      }
      nextWeek: {
        inPipeline: number
        openedSoFar: number
        driveThru: number
        is24By7: number
        franchise: number
        corporate: number
      }
      thisMonth: {
        inPipeline: number
        openedSoFar: number
        driveThru: number
        is24By7: number
        franchise: number
        corporate: number
      }
      nextMonth: {
        inPipeline: number
        openedSoFar: number
        driveThru: number
        is24By7: number
        franchise: number
        corporate: number
      }
      thisQuarter: {
        inPipeline: number
        openedSoFar: number
        driveThru: number
        is24By7: number
        franchise: number
        corporate: number
      }
      nextQuarter: {
        inPipeline: number
        openedSoFar: number
        driveThru: number
        is24By7: number
        franchise: number
        corporate: number
      }
      thisYear: {
        inPipeline: number
        openedSoFar: number
        driveThru: number
        is24By7: number
        franchise: number
        corporate: number
        monthlyBreakdown: {
          january: { total: number; franchise: number; corporate: number }
          february: { total: number; franchise: number; corporate: number }
          march: { total: number; franchise: number; corporate: number }
          april: { total: number; franchise: number; corporate: number }
          may: { total: number; franchise: number; corporate: number }
          june: { total: number; franchise: number; corporate: number }
          july: { total: number; franchise: number; corporate: number }
          august: { total: number; franchise: number; corporate: number }
          september: { total: number; franchise: number; corporate: number }
          october: { total: number; franchise: number; corporate: number }
          november: { total: number; franchise: number; corporate: number }
          december: { total: number; franchise: number; corporate: number }
        }
      }
      nextYear: {
        inPipeline: number
        openedSoFar: number
        driveThru: number
        is24By7: number
        franchise: number
        corporate: number
        monthlyBreakdown: {
          january: { total: number; franchise: number; corporate: number }
          february: { total: number; franchise: number; corporate: number }
          march: { total: number; franchise: number; corporate: number }
          april: { total: number; franchise: number; corporate: number }
          may: { total: number; franchise: number; corporate: number }
          june: { total: number; franchise: number; corporate: number }
          july: { total: number; franchise: number; corporate: number }
          august: { total: number; franchise: number; corporate: number }
          september: { total: number; franchise: number; corporate: number }
          october: { total: number; franchise: number; corporate: number }
          november: { total: number; franchise: number; corporate: number }
          december: { total: number; franchise: number; corporate: number }
        }
      }
      noOpeningDate: {
        total: number
        driveThru: number
        is24By7: number
        franchise: number
        corporate: number
      }
    }
  } | null
  permClosedRestaurantsMeta: { totalRows: number; pages: number } | null
}

// For allocation restaurants, we return the data directly
type AllocationRestaurantsResponse = Gyg365RestoList[]

interface StoreResponse {
  data: any
}

interface StoreUpdateError {
  error: string
  errors: any[]
  message?: string
  statusCode: number
}

interface UserUpdateThunkPayload {
  id: string
  data: UpdateResto
}
interface GetStoreLicensesResponse {
  data: LicenseUpdate[]
}
interface StoreLicensesResponse {
  data: LicenseUpdate
}
interface InitialFilterResponse {
  data: InitialFilter
}
interface InitialStoreResponse {
  data: InitialState
}

interface StoreLicensesUpdatePayload {
  id: number
  data: LicenseUpdate
}

interface LicenseUpload {
  id: number
  file: File
}

// Google Places Address Validation Response
interface GooglePlacesAddressResponse {
  isValid: boolean
  confidence: 'HIGH' | 'MEDIUM' | 'LOW'
  formatted_address: string
  place_id: string
  coordinates: {
    lat: number
    lng: number
  }
  address_components: {
    street_number?: string
    route?: string
    locality?: string
    administrative_area_level_1?: string
    administrative_area_level_2?: string
    postal_code?: string
    country?: string
    subpremise?: string
  }
  suggestions?: Array<{
    formatted_address: string
    geometry: {
      location: {
        lat: number
        lng: number
      }
      viewport?: {
        northeast: { lat: number; lng: number }
        southwest: { lat: number; lng: number }
      }
    }
    icon?: string
    icon_background_color?: string
    icon_mask_base_uri?: string
    name: string
    place_id: string
    reference: string
    types: string[]
  }>
}

const initialState: InitialState = {
  gyg365restros: [],
  exportGyg365restro: [],
  allocationRestaurants: [],
  gyg365resto: [],
  gygLicenses: [],
  restoData: undefined,
  restoDataWithUsers: undefined,
  gygLicense: undefined,
  errors: null,
  success: undefined,
  pending: false,
  get365restroPending: false,
  get365restroWithUsersPending: false,
  export365restroPending: false,
  allocationRestaurantsPending: false,
  update365restroPending: false,
  updateRestaurantIsActivePending: false,
  updateRestaurantSustainabilityPending: false,
  validateYextEntityIdPending: false,
  yextValidationError: null,
  yextValidationSuccess: false,
  yextValidationErrorState: false,

  // Google Places validation state
  validateGooglePlacesAddressPending: false,
  googlePlacesValidationResult: null,
  googlePlacesValidationError: null,
  createStorePending: false,
  menuSections: [],
  menuSectionsPending: false,
  menuProduct: [],
  channelList: [],
  menuModelPending: false,
  menuPending: false,
  menuSuccess: undefined,
  menuError: undefined,
  storeMenu: undefined,
  restaurantMeta: null,
  commonSearchData: {
    searchBar: '',
    searchType: 'restaurantName',
    priceFilter: [],
    offeringFilter: [],
    stateFilter: [],
    statusFilter: [],
    operationalStatusFilter: [],
    overNightService: false,
    formatFilter: [],
    ownershipFilter: []
  },

  // New state for NRO and Perm Closed restaurants
  nroRestaurants: [],
  permClosedRestaurants: [],
  nroRestaurantsPending: false,
  permClosedRestaurantsPending: false,
  nroRestaurantsMeta: null,
  permClosedRestaurantsMeta: null
}

export const createResto365Store = createAsyncThunk<StoreResponse, Gyg365RestoList, Redux>(
  RESTO365_RESTAURANT_CREATE,
  async (storeData, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.post('restaurants', storeData)
      const data = await response.data

      return data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const gyg365RestroListed = createAsyncThunk<{ data: Gyg365RestoList[]; meta: any }, any, Redux>(
  RESTO365_ALL_RESTAURANT,
  async (params, { extra }) => {
    // Transform params to match backend expectations (shallow-copy filters so we don't mutate caller's state)
    const transformedParams = {
      ...params,
      page: (params.page ?? 0) + 1, // Convert 0-based pagination to 1-based for backend
      pageSize: params.pageSize ?? 10,
      filters: params.filters && typeof params.filters === 'object' ? { ...params.filters } : params.filters
    }

    // Extract operationalStatus from filters if it exists and convert to status array.
    // Only use params.filters.operationalStatus - never fall back to Redux saved state here,
    // so that removing the chip (clearing from params) immediately affects the API request.
    let statusArray = transformedParams.status

    if (transformedParams.filters && transformedParams.filters.operationalStatus) {
      statusArray = transformedParams.filters.operationalStatus

      // Remove operationalStatus from filters (only from our copy) since we're converting it to top-level status
      delete transformedParams.filters.operationalStatus
    }

    // statusFilter is UI-only (for Active+In-Active chips); do not send to API
    if (transformedParams.filters) {
      delete transformedParams.filters.statusFilter
    }

    // When operationalStatus is not in params.filters (user cleared it or never set it), use default all statuses.
    // Do not use Redux commonSearchData.operationalStatusFilter so that chip remove/clear takes effect immediately.
    if (!statusArray || statusArray.length === 0) {
      statusArray = ['open', 'futureOpening', 'permClosed']
    }

    transformedParams.status = statusArray

    // Handle status array to send multiple individual status parameters
    // Convert from status: ['open', 'futureOpening', 'permClosed']
    // to individual status=open&status=futureOpening&status=permClosed parameters
    const finalParams = { ...transformedParams }
    if (Array.isArray(finalParams.status)) {
      // Remove the status array and let URLSearchParams handle multiple values
      const statusArray = finalParams.status
      delete finalParams.status

      // Create URLSearchParams to properly format multiple status parameters
      const searchParams = new URLSearchParams()

      // Add all non-status parameters
      Object.entries(finalParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          // Handle filters object specially
          if (key === 'filters' && typeof value === 'object') {
            // Serialize filters as individual query parameters
            Object.entries(value as Record<string, any>).forEach(([filterKey, filterValue]) => {
              if (filterValue !== undefined && filterValue !== null) {
                searchParams.append(filterKey, String(filterValue))
              }
            })
          } else {
            searchParams.append(key, String(value))
          }
        }
      })

      // Add multiple status parameters
      statusArray.forEach((status: string) => {
        searchParams.append('status', status)
      })

      // Make the request with properly formatted query string
      const responseData = await extra.httpClient(`restaurants?${searchParams.toString()}`)

      return {
        data: responseData.data.data,
        meta: responseData.data.meta
      }
    }

    const responseData = await extra.httpClient('restaurants', { params: transformedParams })

    return {
      data: responseData.data.data,
      meta: responseData.data.meta
    }
  }
)

/** Paginated admin restaurant list: slim rows + optional embedded delegations / disabled summary */
export const gyg365AdminListListed = createAsyncThunk<{ data: Gyg365RestoList[]; meta: any }, any, Redux>(
  RESTO365_ADMIN_LIST_RESTAURANT,
  async (params, { extra }) => {
    const transformedParams = {
      ...params,
      page: (params.page ?? 0) + 1,
      pageSize: params.pageSize ?? 10,
      filters: params.filters && typeof params.filters === 'object' ? { ...params.filters } : params.filters
    }

    let statusArray = transformedParams.status

    if (transformedParams.filters && transformedParams.filters.operationalStatus) {
      statusArray = transformedParams.filters.operationalStatus
      delete transformedParams.filters.operationalStatus
    }

    if (transformedParams.filters) {
      delete transformedParams.filters.statusFilter
    }

    if (!statusArray || statusArray.length === 0) {
      statusArray = ['open', 'futureOpening', 'permClosed']
    }

    transformedParams.status = statusArray

    const appendAdminListFlags = (searchParams: URLSearchParams) => {
      searchParams.append('includeUsers', 'true')
      searchParams.append('includeDelegations', 'true')
      searchParams.append('includeDisabledSummary', 'true')
    }

    const finalParams = { ...transformedParams }
    if (Array.isArray(finalParams.status)) {
      const statusArr = finalParams.status
      delete finalParams.status

      const searchParams = new URLSearchParams()

      Object.entries(finalParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === 'filters' && typeof value === 'object') {
            Object.entries(value as Record<string, any>).forEach(([filterKey, filterValue]) => {
              if (filterValue !== undefined && filterValue !== null) {
                searchParams.append(filterKey, String(filterValue))
              }
            })
          } else {
            searchParams.append(key, String(value))
          }
        }
      })

      statusArr.forEach((status: string) => {
        searchParams.append('status', status)
      })

      appendAdminListFlags(searchParams)

      const responseData = await extra.httpClient(`restaurants/admin-list?${searchParams.toString()}`)

      return {
        data: responseData.data.data,
        meta: responseData.data.meta
      }
    }

    const responseData = await extra.httpClient('restaurants/admin-list', {
      params: {
        ...transformedParams,
        includeUsers: true,
        includeDelegations: true,
        includeDisabledSummary: true
      }
    })

    return {
      data: responseData.data.data,
      meta: responseData.data.meta
    }
  }
)

export const exportGyg365Restro = createAsyncThunk<Gyg365RestoList[], QueryParams, Redux>(
  EXPORT_RESTO365_RESTAURANT,
  async (exportAllParams: QueryParams, { extra, rejectWithValue }) => {
    try {
      // Transform params to match backend expectations (shallow-copy filters so we don't mutate caller's state)
      const transformedParams: any = {
        ...exportAllParams,
        page: (exportAllParams.page ?? 0) + 1, // Convert 0-based pagination to 1-based for backend
        pageSize: exportAllParams.pageSize ?? 10,
        filters:
          exportAllParams.filters && typeof exportAllParams.filters === 'object'
            ? { ...exportAllParams.filters }
            : exportAllParams.filters
      }

      // Extract operationalStatus from filters if it exists and convert to status array.
      // Only use params; do not use Redux saved state so that clearing the filter takes effect.
      let statusArray = transformedParams.status
      if (transformedParams.filters && transformedParams.filters.operationalStatus) {
        statusArray = transformedParams.filters.operationalStatus

        // Remove operationalStatus from filters (only from our copy) since we're converting it to top-level status
        delete transformedParams.filters.operationalStatus
      }

      if (transformedParams.filters) {
        delete transformedParams.filters.statusFilter
      }

      if (!statusArray || statusArray.length === 0) {
        statusArray = ['open', 'futureOpening', 'permClosed']
      }

      transformedParams.status = statusArray

      // Handle status array to send multiple individual status parameters if it exists
      const finalParams = { ...transformedParams }
      if (Array.isArray(finalParams.status)) {
        // Remove the status array and let URLSearchParams handle multiple values
        const statusArray = finalParams.status
        delete finalParams.status

        // Create URLSearchParams to properly format multiple status parameters
        const searchParams = new URLSearchParams()

        // Add all non-status parameters
        Object.entries(finalParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            // Handle filters object specially
            if (key === 'filters' && typeof value === 'object') {
              // Serialize filters as individual query parameters
              Object.entries(value as Record<string, any>).forEach(([filterKey, filterValue]) => {
                if (filterValue !== undefined && filterValue !== null) {
                  searchParams.append(filterKey, String(filterValue))
                }
              })
            } else {
              searchParams.append(key, String(value))
            }
          }
        })

        // Add multiple status parameters
        statusArray.forEach((status: string) => {
          searchParams.append('status', status)
        })

        // Make the request with properly formatted query string
        const responseData = await extra.httpClient(`restaurants?${searchParams.toString()}`)

        return responseData.data.data
      }

      const responseData = await extra.httpClient('restaurants', { params: transformedParams })

      return responseData.data.data
    } catch (error: any) {
      return rejectWithValue(error.response)
    }
  }
)

// New async thunk for NRO (New Restaurants Opening) restaurants
export const gyg365NroRestroListed = createAsyncThunk<{ data: Gyg365RestoList[]; meta: any }, any, Redux>(
  RESTO365_NRO_RESTAURANT,
  async (params, { extra }) => {
    // Transform params to match backend expectations
    const transformedParams = {
      ...params,
      page: (params.page ?? 0) + 1, // Convert 0-based pagination to 1-based for backend
      pageSize: params.pageSize ?? 10
    }

    const responseData = await extra.httpClient('restaurants/future-opening', { params: transformedParams })

    return {
      data: responseData.data.data,
      meta: responseData.data.meta
    }
  }
)

// New async thunk for Perm Closed restaurants
export const gyg365PermClosedRestroListed = createAsyncThunk<{ data: Gyg365RestoList[]; meta: any }, any, Redux>(
  RESTO365_PERM_CLOSED_RESTAURANT,
  async (params, { extra }) => {
    // Transform params to match backend expectations
    const transformedParams = {
      ...params,
      page: (params.page ?? 0) + 1, // Convert 0-based pagination to 1-based for backend
      pageSize: params.pageSize ?? 10
    }

    const responseData = await extra.httpClient('restaurants/permanently-closed', { params: transformedParams })

    return {
      data: responseData.data.data,
      meta: responseData.data.meta
    }
  }
)

export const getAllocationRestaurants = createAsyncThunk<AllocationRestaurantsResponse, QueryParams, Redux>(
  'RESTO365_GET_ALLOCATION_RESTAURANTS',
  async (params: QueryParams, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient('restaurants/allocation/with-users-and-franchise', { params })

      return responseData.data
    } catch (error: any) {
      // Only return the error data, not the full response object
      if (error.response?.data) {
        return rejectWithValue(error.response.data)
      }

      return rejectWithValue(error.message || 'Failed to fetch allocation restaurants')
    }
  }
)

export const get365RestoByIdData = createAsyncThunk<StoreResponse, number, Redux>(
  RESTO365_GET_RESTAURANT_BY_ID,
  async (Id: number, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`restaurants/${Id}`)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }

      return rejectWithValue(error.message || 'Failed to fetch restaurant data')
    }
  }
)

export const get365RestoByIdWithUsers = createAsyncThunk<RestaurantWithUsersResponse, number, Redux>(
  'RESTO365_GET_RESTAURANT_BY_ID_WITH_USERS',
  async (Id: number, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`restaurants/${Id}/with-users`)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }

      return rejectWithValue(error.message || 'Failed to fetch restaurant data with users')
    }
  }
)

export const getFranchiseRestaurantsByIds = createAsyncThunk<StoreResponse, number[], Redux>(
  RESTO365_GET_FRANCHISE_RESTAURANTS_BY_IDS,
  async (restaurantIds: number[], { extra, rejectWithValue }) => {
    try {
      const idsParam = restaurantIds.join(',')
      const response = await extra.httpClient(`restaurants/franchise/by-ids?ids=${idsParam}`)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }

      return rejectWithValue(error.message || 'Failed to fetch franchise restaurants data')
    }
  }
)

export const updateFranchiseId = createAsyncThunk<
  StoreResponse,
  { restaurantIds: number[]; franchiseId: number },
  Redux
>(RESTO365_RESTAURANT_UPDATE_FRANCHISE_ID, async ({ restaurantIds, franchiseId }, { extra }) => {
  const response = await extra.httpClient(`restaurants/bulk/franchise`, {
    method: 'PATCH',
    data: { restaurantIds, franchiseId }
  })

  return response.data
})
export const update365Restro = createAsyncThunk<StoreResponse, UserUpdateThunkPayload, Redux>(
  RESTO365_UPDATE_RESTAURANT,
  async ({ id, data }, { extra, rejectWithValue }) => {
    try {
      data.postCode = data.postalCode
      data.city = data.suburb
      data.iceMachine = data.icedCoffee

      const response = await extra.httpClient(`restaurants/${id}`, { method: 'PATCH', data })

      // Wrap response.data in a data property to match StoreResponse interface
      return { data: response.data }
    } catch (error: any) {
      if (error.response) {
        const errorData = error.response.data

        // Handle specific validation errors with better messages
        if (errorData?.message || errorData?.error) {
          const originalMessage = errorData.message || errorData.error

          // Check for UUID format errors specifically for uberEatsId
          if (
            originalMessage.toLowerCase().includes('ubereatsid') ||
            originalMessage.toLowerCase().includes('uber eats') ||
            originalMessage.toLowerCase().includes('uuid')
          ) {
            return rejectWithValue({
              message: 'Uber Eats ID must be in UUID format (e.g., 12345678-1234-1234-1234-123456789abc)',
              code: 'VALIDATION_ERROR',
              originalMessage
            })
          }

          // Check for general UUID validation errors
          if (originalMessage.toLowerCase().includes('invalid') && originalMessage.toLowerCase().includes('format')) {
            return rejectWithValue({
              message: `${originalMessage}. Please use UUID format (e.g., 12345678-1234-1234-1234-123456789abc)`,
              code: 'VALIDATION_ERROR'
            })
          }
        }

        return rejectWithValue(errorData)
      }

      // Handle cases where there's no response (network errors, timeouts, etc.)
      return rejectWithValue({
        message: error.message || 'Network error occurred',
        code: error.code || 'NETWORK_ERROR'
      })
    }
  }
)

// New async thunk for PATCH /restaurant/:id/is-active
export const updateRestaurantIsActive = createAsyncThunk<
  StoreResponse,
  { id: number; isActive: number; inActiveReason?: string },
  Redux
>('RESTO365_UPDATE_RESTAURANT_IS_ACTIVE', async ({ id, isActive, inActiveReason }, { extra, rejectWithValue }) => {
  try {
    const data: { isActive: number; inActiveReason?: string } = { isActive }

    // Only include inActiveReason if provided
    if (inActiveReason !== undefined) {
      data.inActiveReason = inActiveReason
    }

    const response = await extra.httpClient(`restaurants/${id}/is-active`, {
      method: 'PATCH',
      data
    })

    return response.data
  } catch (error: any) {
    if (error.response) {
      return rejectWithValue(error.response.data)
    }
  }
})

export const updateRestaurantSustainability = createAsyncThunk<StoreResponse, UserUpdateThunkPayload, Redux>(
  RESTO365_UPDATE_RESTAURANT_SUSTAINABILITY,
  async ({ id, data }, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`restaurants/${id}/sustainability`, { method: 'PATCH', data })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const validateYextEntityId = createAsyncThunk<
  {
    existsInYextPlatform: boolean
    alreadyAssignedToAnotherRestaurant: boolean
    belongToCurrentStore: boolean | string
    message: string
  },
  { yextEntityId: string; currentRestaurantId?: number },
  Redux
>(RESTO365_VALIDATE_YEXT_ENTITY_ID, async ({ yextEntityId, currentRestaurantId }, { extra, rejectWithValue }) => {
  try {
    let url = `validateYextEntityId/restaurant/${yextEntityId}`
    if (currentRestaurantId) {
      url += `?currentRestaurantId=${currentRestaurantId}`
    }
    const response = await extra.httpClient.get(url)

    return response.data.data
  } catch (error: any) {
    if (error.response?.status === 404) {
      return rejectWithValue('Restaurant not found in Yext platform')
    }
    if (error.response?.status === 500) {
      return rejectWithValue('Internal server error while validating Yext Entity ID')
    }

    return rejectWithValue(error.response?.data?.message || 'Failed to validate Yext Entity ID')
  }
})

export const validateGooglePlacesAddress = createAsyncThunk<GooglePlacesAddressResponse, { address: string }, Redux>(
  RESTO365_VALIDATE_GOOGLE_PLACES_ADDRESS,
  async ({ address }, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.get(
        `google_places/restaurant-address-validation?address=${encodeURIComponent(address.trim())}`
      )

      return response.data.data // Response has a data wrapper
    } catch (error: any) {
      if (error.response?.status === 404) {
        return rejectWithValue('Address validation service not found')
      }
      if (error.response?.status === 500) {
        return rejectWithValue('Internal server error while validating address')
      }

      return rejectWithValue(error.response?.data?.message || 'Failed to validate address')
    }
  }
)

/* License Data */
export const getStoreLicensesListById = createAsyncThunk<GetStoreLicensesResponse, number, Redux>(
  RESTO365_RESTAURANT_LICENSE_LIST,
  async (Id: number, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`resto365-restaurant-licences?restaurantId=${Id}`)

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const createStoreLicensesList = createAsyncThunk<StoreLicensesResponse, LicenseCreate, Redux>(
  RESTO365_RESTAURANT_CREATE_LICENSE_LIST,
  async (licenseData, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient.post(`resto365-restaurant-licences`, licenseData)
      const data = await response.data

      return data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const updateStoreLicenses = createAsyncThunk<StoreLicensesResponse, StoreLicensesUpdatePayload, Redux>(
  RESTO365_RESTAURANT_UPDATE_LICENSE_LIST,
  async ({ id, data }, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`resto365-restaurant-licences/${id}`, { method: 'PATCH', data })

      return response.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const uploadLicenseFile = createAsyncThunk<LicenseUpload, any, Redux>(
  RESTO365_RESTAURANT_UPDATE_LICENSE_UPLOAD,
  async ({ id, file }, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient.post(`resto365-restaurant-licences/imageUpload/${id}`, file)

      return responseData.data
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const commonSearchInitial = createAsyncThunk<InitialFilterResponse, void, Redux>(
  RESTO365_RESTAURANT_COMMON_SEARCH,
  async () => {
    return {
      data: initialState.commonSearchData
    }
  }
)

export const updateCommonSearchData = createAsyncThunk<InitialFilterResponse, InitialFilter, Redux>(
  RESTO365_RESTAURANT_COMMON_SEARCH_UPDATE,
  async commonSearchData => {
    return {
      data: commonSearchData
    }
  }
)

export const clearReducerOnLogout = createAsyncThunk<InitialStoreResponse, void, Redux>(
  RESTO365_RESTAURANT_RESET_REDUCER,
  async () => {
    return {
      data: initialState
    }
  }
)

export const clearErrorData = createAsyncThunk<void, void, Redux>(RESTO365_RESTAURANT_ERROR_RESET, async () => {
  // This action doesn't need to return any data
  // The reducer will handle clearing only error-related state
})

export const gyg365RestoList = createSlice({
  name: RESTO365_RESTAURANT,
  initialState,
  reducers: {
    // Custom reducer for updating test mode locally without triggering update365Restro
    updateTestModeLocal: (state, action) => {
      const { id, isTest } = action.payload

      // Update restoData if it exists and matches the updated restaurant
      if (state.restoData && state.restoData.id === id) {
        ;(state.restoData as Gyg365RestoList & { isTest: number | boolean }).isTest = isTest
      }

      // Update restoDataWithUsers if it exists and matches the updated restaurant
      if (state.restoDataWithUsers && state.restoDataWithUsers.id === id) {
        ;(state.restoDataWithUsers as RestaurantWithUsers & { isTest: number | boolean }).isTest = isTest
      }
    }
  },
  extraReducers: builder => {
    builder

      // Create Stores
      .addCase(createResto365Store.fulfilled, (state, action) => {
        state.restoData = action.payload.data
        state.success = 'Restaurant created Successfully'
        state.errors = null
        state.createStorePending = false
      })
      .addCase(createResto365Store.pending, state => {
        state.createStorePending = true
        state.success = undefined
      })
      .addCase(createResto365Store.rejected, (state, action) => {
        let errors
        if (action.payload) {
          errors = action.payload
        } else {
          errors = action.error
        }
        state.errors = errors
        state.success = undefined
        state.createStorePending = false
      })

      // All Stores get
      .addCase(gyg365RestroListed.fulfilled, (state, action) => {
        state.gyg365restros = action.payload.data
        state.restaurantMeta = action.payload.meta
        state.gygLicenses = []
        state.gyg365resto = []
        state.restoData = undefined
        state.success = undefined
        state.get365restroPending = false
        state.errors = null
        state.menuSuccess = undefined
        state.menuError = undefined
      })
      .addCase(gyg365RestroListed.pending, state => {
        state.success = undefined
        state.restoData = undefined
        state.get365restroPending = true
      })
      .addCase(gyg365RestroListed.rejected, state => {
        state.get365restroPending = false
        state.success = undefined
      })

      // Admin list (slim rows) — same grid state as legacy list
      .addCase(gyg365AdminListListed.fulfilled, (state, action) => {
        state.gyg365restros = action.payload.data
        state.restaurantMeta = action.payload.meta
        state.gygLicenses = []
        state.gyg365resto = []
        state.restoData = undefined
        state.success = undefined
        state.get365restroPending = false
        state.errors = null
        state.menuSuccess = undefined
        state.menuError = undefined
      })
      .addCase(gyg365AdminListListed.pending, state => {
        state.success = undefined
        state.restoData = undefined
        state.get365restroPending = true
      })
      .addCase(gyg365AdminListListed.rejected, state => {
        state.get365restroPending = false
        state.success = undefined
      })

      // NRO Restaurants
      .addCase(gyg365NroRestroListed.fulfilled, (state, action) => {
        state.nroRestaurants = action.payload.data
        state.nroRestaurantsMeta = action.payload.meta
        state.nroRestaurantsPending = false
        state.errors = null
      })
      .addCase(gyg365NroRestroListed.pending, state => {
        state.nroRestaurantsPending = true
      })
      .addCase(gyg365NroRestroListed.rejected, state => {
        state.nroRestaurantsPending = false
      })

      // Perm Closed Restaurants
      .addCase(gyg365PermClosedRestroListed.fulfilled, (state, action) => {
        state.permClosedRestaurants = action.payload.data
        state.permClosedRestaurantsMeta = action.payload.meta
        state.permClosedRestaurantsPending = false
        state.errors = null
      })
      .addCase(gyg365PermClosedRestroListed.pending, state => {
        state.permClosedRestaurantsPending = true
      })
      .addCase(gyg365PermClosedRestroListed.rejected, state => {
        state.permClosedRestaurantsPending = false
      })

      // Export Store
      .addCase(exportGyg365Restro.fulfilled, (state, action) => {
        state.exportGyg365restro = action.payload
        state.gygLicenses = []
        state.gyg365resto = []
        state.restoData = undefined
        state.success = undefined
        state.export365restroPending = false
        state.errors = null
        state.menuSuccess = undefined
        state.menuError = undefined
      })
      .addCase(exportGyg365Restro.pending, state => {
        state.success = undefined
        state.restoData = undefined
        state.export365restroPending = true
      })
      .addCase(exportGyg365Restro.rejected, state => {
        state.export365restroPending = false
        state.success = undefined
      })

      // Allocation Restaurants
      .addCase(getAllocationRestaurants.fulfilled, (state, action) => {
        state.allocationRestaurants = action.payload
        state.allocationRestaurantsPending = false
        state.errors = null
      })
      .addCase(getAllocationRestaurants.pending, state => {
        state.allocationRestaurantsPending = true
      })
      .addCase(getAllocationRestaurants.rejected, (state, action) => {
        state.allocationRestaurantsPending = false
        state.errors = action.payload || action.error
      })

      // Single Store By Id
      .addCase(get365RestoByIdData.fulfilled, (state, action) => {
        state.restoData = action.payload.data
        state.success = undefined
        state.pending = false
        state.errors = null
      })
      .addCase(get365RestoByIdData.pending, state => {
        state.pending = true
        state.success = undefined
        state.errors = null
      })
      .addCase(get365RestoByIdData.rejected, (state, action) => {
        state.pending = false
        state.errors = action.payload || action.error
        state.success = undefined
      })

      // Single Store By Id With Users
      .addCase(get365RestoByIdWithUsers.fulfilled, (state, action) => {
        state.restoDataWithUsers = action.payload.data
        state.success = undefined
        state.get365restroWithUsersPending = false
        state.errors = null
      })
      .addCase(get365RestoByIdWithUsers.pending, state => {
        state.get365restroWithUsersPending = true
        state.success = undefined
        state.errors = null
      })
      .addCase(get365RestoByIdWithUsers.rejected, (state, action) => {
        state.get365restroWithUsersPending = false
        state.errors = action.payload || action.error
        state.success = undefined
      })

      // Update Franchise Id
      .addCase(updateFranchiseId.fulfilled, (state, action) => {
        state.restoData = action.payload.data
        state.success = undefined
        state.pending = false
        state.errors = null
      })
      .addCase(updateFranchiseId.pending, state => {
        state.pending = true
        state.success = undefined
      })
      .addCase(updateFranchiseId.rejected, state => {
        state.pending = false
      })

      // Update Store
      .addCase(update365Restro.fulfilled, (state, action) => {
        state.update365restroPending = false
        state.gyg365resto = (action.payload as StoreResponse).data

        // Also update restoData if it exists and matches the updated restaurant
        if (state.restoData && state.restoData.id === (action.payload as StoreResponse).data.id) {
          state.restoData = (action.payload as StoreResponse).data
        }

        // Also update restoDataWithUsers if it exists and matches the updated restaurant
        if (state.restoDataWithUsers && state.restoDataWithUsers.id === (action.payload as StoreResponse).data.id) {
          state.restoDataWithUsers = (action.payload as StoreResponse).data
        }

        state.errors = null
        state.success = 'Restaurant detail are successfully updated'
      })
      .addCase(update365Restro.pending, state => {
        state.update365restroPending = true
      })
      .addCase(update365Restro.rejected, (state, action) => {
        let errors
        if (action.payload !== undefined && (action.payload as StoreUpdateError).errors) {
          errors = (action.payload as StoreUpdateError).errors
        } else {
          errors = action.payload
        }
        state.errors = errors
        state.success = undefined
        state.update365restroPending = false
      })

      // Update Restaurant Is Active
      .addCase(updateRestaurantIsActive.fulfilled, (state, action) => {
        state.updateRestaurantIsActivePending = false

        // Extract the restaurant ID and new status from the action meta
        const { id, isActive, inActiveReason } = action.meta.arg

        // Update gyg365resto array if it exists
        if (state.gyg365resto && Array.isArray(state.gyg365resto)) {
          state.gyg365resto = state.gyg365resto.map(resto =>
            resto.id === id ? { ...resto, isActive, ...(inActiveReason && { inActiveReason }) } : resto
          )
        }

        // Update restoData if it exists and matches the updated restaurant
        if (state.restoData && state.restoData.id === id) {
          state.restoData = {
            ...state.restoData,
            isActive,
            ...(inActiveReason && { inActiveReason })
          }
        }

        // Update restoDataWithUsers if it exists and matches the updated restaurant
        if (state.restoDataWithUsers && state.restoDataWithUsers.id === id) {
          state.restoDataWithUsers = {
            ...state.restoDataWithUsers,
            isActive,
            ...(inActiveReason && { inActiveReason })
          }
        }

        state.errors = null

        // Don't set success message here since we handle it in the component
        // state.success = 'Restaurant active status updated successfully'
      })
      .addCase(updateRestaurantIsActive.pending, state => {
        state.updateRestaurantIsActivePending = true
      })
      .addCase(updateRestaurantIsActive.rejected, (state, action) => {
        state.updateRestaurantIsActivePending = false
        state.errors = action.payload || action.error
        state.success = undefined
      })

      // Update Store Sustainability
      .addCase(updateRestaurantSustainability.fulfilled, (state, action) => {
        state.updateRestaurantSustainabilityPending = false

        const responseData = (action.payload as StoreResponse).data

        // Update gyg365resto array if it exists
        if (state.gyg365resto && Array.isArray(state.gyg365resto)) {
          state.gyg365resto = state.gyg365resto.map(resto =>
            resto.id === responseData.id ? { ...resto, sustainability: responseData.sustainability } : resto
          )
        }

        // Update restoData if it exists and matches the updated restaurant
        if (state.restoData && state.restoData.id === responseData.id) {
          state.restoData = { ...state.restoData, sustainability: responseData.sustainability }
        }

        // Update restoDataWithUsers if it exists and matches the updated restaurant
        if (state.restoDataWithUsers && state.restoDataWithUsers.id === responseData.id) {
          state.restoDataWithUsers = { ...state.restoDataWithUsers, sustainability: responseData.sustainability }
        }

        state.errors = null
        state.success = 'Restaurant detail are successfully updated'
      })
      .addCase(updateRestaurantSustainability.pending, state => {
        state.updateRestaurantSustainabilityPending = true
      })
      .addCase(updateRestaurantSustainability.rejected, (state, action) => {
        let errors
        if (action.payload !== undefined && (action.payload as StoreUpdateError).errors) {
          errors = (action.payload as StoreUpdateError).errors
        } else {
          errors = action.payload
        }
        state.errors = errors
        state.success = undefined
        state.updateRestaurantSustainabilityPending = false
      })

      // Validate Yext Entity ID
      .addCase(validateYextEntityId.fulfilled, (state, action) => {
        state.validateYextEntityIdPending = false
        state.errors = null

        // Check if the Yext Entity ID is valid
        // Valid ID: existsInYextPlatform=true AND (alreadyAssignedToAnotherRestaurant=false OR belongToCurrentStore=true)
        const isBelongToCurrentStore =
          action.payload.belongToCurrentStore === true || action.payload.belongToCurrentStore === 'true'

        if (
          !action.payload.existsInYextPlatform ||
          (action.payload.alreadyAssignedToAnotherRestaurant && !isBelongToCurrentStore)
        ) {
          state.yextValidationError = action.payload.message
          state.yextValidationSuccess = false
          state.yextValidationErrorState = true
        } else {
          state.yextValidationError = null
          state.yextValidationSuccess = true
          state.yextValidationErrorState = false
        }
      })
      .addCase(validateYextEntityId.pending, state => {
        state.validateYextEntityIdPending = true
        state.yextValidationError = null
        state.yextValidationSuccess = false
        state.yextValidationErrorState = false
      })
      .addCase(validateYextEntityId.rejected, (state, action) => {
        state.validateYextEntityIdPending = false
        state.yextValidationError = (action.payload as string) || 'Failed to validate Yext Entity ID'
        state.yextValidationSuccess = false
        state.yextValidationErrorState = true
      })

      // Validate Google Places Address
      .addCase(validateGooglePlacesAddress.fulfilled, (state, action) => {
        state.validateGooglePlacesAddressPending = false
        state.googlePlacesValidationResult = action.payload
        state.googlePlacesValidationError = null
      })
      .addCase(validateGooglePlacesAddress.pending, state => {
        state.validateGooglePlacesAddressPending = true
        state.googlePlacesValidationResult = null
        state.googlePlacesValidationError = null
      })
      .addCase(validateGooglePlacesAddress.rejected, (state, action) => {
        state.validateGooglePlacesAddressPending = false
        state.googlePlacesValidationResult = null
        state.googlePlacesValidationError = (action.payload as string) || 'Failed to validate address'
      })

      //get menuSection list
      .addCase(getmenuSectionsList.fulfilled, (state, action) => {
        const payload = action.payload as { data?: SectionProduct[] } | undefined
        const sections = payload?.data
        state.menuSections = Array.isArray(sections) ? sections : []
        state.menuSectionsPending = false
        state.menuPending = false
        state.menuSuccess = undefined
      })
      .addCase(getmenuSectionsList.pending, state => {
        state.menuError = undefined
        state.menuSectionsPending = true
        state.menuPending = true
        state.menuSuccess = undefined
      })
      .addCase(getmenuSectionsList.rejected, (state, error) => {
        const payload: any = error.payload

        if (payload?.message) state.menuError = payload.message
        else state.menuError = error.error?.message ?? 'Failed to load menu sections.'
        state.menuSectionsPending = false
        state.menuPending = false
        state.menuSuccess = undefined
      })

      //update menu product status
      .addCase(enableDisableProduct.fulfilled, (state, action) => {
        const payload = action.payload
        if (!payload) {
          state.menuPending = false

          return
        }
        const updatedProducts = payload.updated || []

        // Update menu sections with the updated products
        state.menuSections = state.menuSections.map(product => {
          const updatedProduct = updatedProducts.find(uProduct => uProduct.id === product.id)
          if (updatedProduct) {
            product.isEnable = updatedProduct.isEnable
          }

          return product
        })

        // Store menu generation information
        state.menuGenerationTriggered = payload.menuGenerationTriggered || false
        state.menuGenerationChannels = payload.menuGenerationChannels || []

        state.menuPending = false
        state.menuSuccess = 'Ingredients are updated successfully'
      })
      .addCase(enableDisableProduct.pending, state => {
        state.menuError = undefined
        state.menuPending = true
        state.menuSuccess = undefined
      })
      .addCase(enableDisableProduct.rejected, (state, error) => {
        const payload: any = error.payload

        if (payload?.message) state.menuError = payload.message
        else state.menuError = error.error?.message ?? 'Failed to update ingredients.'
        state.menuPending = false
        state.menuSuccess = undefined
      })

      //get products
      .addCase(getProducts.fulfilled, (state, action) => {
        const payload = action.payload as { data?: unknown } | undefined
        state.menuProduct = (payload?.data ?? []) as MenuInitialState['menuProduct']
        state.menuModelPending = false
        state.menuSuccess = undefined
      })
      .addCase(getProducts.pending, state => {
        state.menuError = undefined
        state.menuModelPending = true
        state.menuSuccess = undefined
      })
      .addCase(getProducts.rejected, (state, error) => {
        const payload: any = error.payload

        if (payload?.message) state.menuError = payload.message
        else state.menuError = error.error?.message ?? 'Failed to load products.'
        state.menuModelPending = false
        state.menuSuccess = undefined
      })

      //get store channels
      .addCase(getChannelList.pending, state => {
        state.menuError = undefined
        state.menuModelPending = true
        state.menuSuccess = undefined
        state.channelList = []
        state.storeMenu = undefined
      })
      .addCase(getChannelList.fulfilled, (state, action) => {
        const payload = action.payload as { data?: unknown } | undefined
        state.channelList = (payload?.data ?? []) as MenuInitialState['channelList']
        state.menuModelPending = false
        state.menuSuccess = undefined
      })
      .addCase(getChannelList.rejected, (state, error) => {
        const payload: any = error.payload

        if (payload?.message) state.menuError = payload.message
        else state.menuError = error.error?.message ?? 'Failed to load channels.'

        state.menuModelPending = false
        state.menuSuccess = undefined
      })

      //get StoreMenu
      .addCase(getStoreMenu.fulfilled, (state, action) => {
        state.storeMenu = action.payload
        state.menuModelPending = false
        state.menuSuccess = undefined
      })
      .addCase(getStoreMenu.pending, state => {
        state.menuError = undefined
        state.menuModelPending = true
        state.menuSuccess = undefined
        state.storeMenu = undefined
      })
      .addCase(getStoreMenu.rejected, (state, error) => {
        const payload: any = error.payload

        if (payload?.message) state.menuError = payload.message
        else state.menuError = error.error?.message ?? 'Failed to load store menu.'
        state.menuModelPending = false
        state.menuSuccess = undefined
      })

      /* Store Items License */
      .addCase(getStoreLicensesListById.fulfilled, (state, action) => {
        state.gygLicenses = action.payload?.data || []
        state.pending = false
        state.success = undefined
      })
      .addCase(getStoreLicensesListById.pending, state => {
        state.pending = true
        state.success = undefined
      })
      .addCase(getStoreLicensesListById.rejected, state => {
        state.pending = false
        state.success = undefined
        state.pending = false
      })

      .addCase(createStoreLicensesList.fulfilled, (state, action) => {
        state.gygLicense = action.payload.data
        state.success = 'License created Successfully'
        state.errors = null
        state.pending = false
      })
      .addCase(createStoreLicensesList.pending, state => {
        state.pending = true
        state.success = undefined
      })
      .addCase(createStoreLicensesList.rejected, (state, action) => {
        let errors
        if (action.payload) {
          errors = action.payload
        } else {
          errors = action.error
        }
        state.errors = errors
        state.success = undefined
        state.pending = false
      })

      // Update Store
      .addCase(updateStoreLicenses.fulfilled, (state, action) => {
        state.success = 'License detail are successfully updated'
        state.gygLicense = action.payload.data
        state.pending = false
        state.errors = null
      })
      .addCase(updateStoreLicenses.pending, state => {
        state.pending = true
        state.success = undefined
      })
      .addCase(updateStoreLicenses.rejected, (state, action) => {
        let errors: any = {}
        if (action.payload !== undefined && (action.payload as StoreUpdateError).errors) {
          errors = (action.payload as StoreUpdateError).message
        } else {
          const errorMessage: string = (action.payload as StoreUpdateError).message as string
          if (errorMessage.includes('Duplicate entry')) {
            errors.message = 'License already exists, please update the existing license'
          } else {
            errors = action.payload
          }
        }
        state.errors = errors
        state.success = undefined
        state.pending = false
      })

      // Update Store
      .addCase(uploadLicenseFile.fulfilled, state => {
        state.pending = false
        state.errors = null
      })
      .addCase(uploadLicenseFile.pending, state => {
        state.pending = true
        state.success = undefined
      })
      .addCase(uploadLicenseFile.rejected, (state, action) => {
        let errors
        if (action.payload !== undefined && (action.payload as StoreUpdateError).errors) {
          errors = (action.payload as StoreUpdateError).message
        } else {
          errors = action.payload
        }
        state.errors = errors
        state.success = undefined
        state.pending = false
      })

      .addCase(commonSearchInitial.fulfilled, state => {
        state.commonSearchData = {
          searchBar: '',
          searchType: 'restaurantName',
          priceFilter: [],
          offeringFilter: [],
          stateFilter: [],
          statusFilter: [],
          operationalStatusFilter: [],
          overNightService: false,
          formatFilter: [],
          ownershipFilter: []
        }
      })

      .addCase(updateCommonSearchData.fulfilled, (state, action) => {
        state.commonSearchData = action.payload.data
      })

      .addCase(clearReducerOnLogout.fulfilled, state => {
        state.gyg365restros = []
        state.gygLicenses = []
        state.gyg365resto = []
        state.restoData = undefined
        state.success = undefined
        state.pending = false
        state.get365restroPending = false
        state.errors = null
        state.menuSuccess = undefined
        state.menuError = undefined
        state.storeMenu = undefined
        state.exportGyg365restro = []
        state.allocationRestaurants = []
        state.allocationRestaurantsPending = false
        state.commonSearchData = {
          searchBar: '',
          searchType: 'restaurantName',
          priceFilter: [],
          offeringFilter: [],
          stateFilter: [],
          statusFilter: [],
          operationalStatusFilter: [],
          overNightService: false,
          formatFilter: [],
          ownershipFilter: []
        }
      })

      .addCase(clearErrorData.fulfilled, state => {
        // Only clear error-related state, preserve all pending states and data
        state.success = undefined
        state.errors = null
        state.yextValidationError = null
        state.yextValidationSuccess = false
        state.yextValidationErrorState = false
        state.googlePlacesValidationResult = null
        state.googlePlacesValidationError = null
        state.menuSuccess = undefined
        state.menuError = undefined
      })
  }
})

// Export the custom action
export const { updateTestModeLocal } = gyg365RestoList.actions

export default gyg365RestoList.reducer
